import React, { Dispatch, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import formatsSvg from 'assets/icons/formats.svg';
import { Store } from 'components/common/types/Store.types';
import { useOnChangePanelVisibility } from 'components/pages/PCM/hooks/useOnChangePanelVisibility';
import SectionButton from 'lib/SectionButton';
import { SectionButtonTheme } from 'lib/SectionButton/SectionButton.types';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { DirectSalesCodeNameModel } from 'components/common/types/DirectSalesCampaign.types';
import { getNoRestrictionsDisplayValue } from 'components/pages/PCM/Products/utils/getNoRestrictionsDisplayValue';
import { sortByCategoryIncludeExclude } from 'components/pages/Planner/PlannerSections/FiltersContent/utils';
import { changeNewProductField, clearItemInNewProduct, hideSecondaryPanel } from 'store/productManagement/reducer';
import { ProductFieldName, SelectionPermission } from 'store/productManagement/reducer.types';
import { CodeNameModel } from 'components/common/types';
import { getIsReadOnly } from 'store/productManagement/selectors';
import { isValueRequired } from '../../utils/isValueRequired';
import { ProductFormatsPanel } from './ProductFormatsPanel';
import { PERMISSION_LABELS } from '../../consts';

const fieldName = ProductFieldName.FORMATS;

const getTheme = (selectedPermission?: SelectionPermission): SectionButtonTheme => {
  return selectedPermission === SelectionPermission.ALL_OPTIONS ? SectionButtonTheme.SOLID : SectionButtonTheme.DASHED;
};

const getNewFormats = (
  selectedFormats: DirectSalesCodeNameModel[],
  formatsToRemove: string[],
  included?: boolean,
): DirectSalesCodeNameModel[] => {
  const values = new Set(formatsToRemove);
  return selectedFormats.filter(
    (value) => !values.has(value.name) || (included !== undefined && value.include !== included),
  );
};

const handleFormatsChangeByInclude = ({
  selectedFormats,
  included,
  dispatch,
  selectedPermission,
  handleConfirm,
}: {
  selectedFormats: DirectSalesCodeNameModel[];
  included?: boolean;
  dispatch: Dispatch<AnyAction>;
  selectedPermission: SelectionPermission;
  handleConfirm: ({
    selectedPermission,
    formats,
  }: {
    selectedPermission: CodeNameModel;
    formats: DirectSalesCodeNameModel[];
  }) => void;
}): void => {
  const newSelectedFormats = selectedFormats.filter((value) => value.include !== included);
  if (!newSelectedFormats.length) {
    dispatch(clearItemInNewProduct({ fieldName }));
    return;
  }

  handleConfirm({
    selectedPermission: { code: selectedPermission, name: PERMISSION_LABELS[selectedPermission] },
    formats: newSelectedFormats,
  });
};

const handleFormatsChange = ({
  selectedFormats,
  formatsToRemove,
  included,
  selectedPermission,
  handleConfirm,
  dispatch,
}: {
  selectedFormats: DirectSalesCodeNameModel[];
  formatsToRemove: string[];
  included?: boolean;
  selectedPermission: { code: string; name: string };
  handleConfirm: ({
    selectedPermission,
    formats,
  }: {
    selectedPermission: CodeNameModel;
    formats: DirectSalesCodeNameModel[];
  }) => void;
  dispatch: Dispatch<AnyAction>;
}): void => {
  const newSelectedFormats = getNewFormats(selectedFormats || [], formatsToRemove, included);

  if (newSelectedFormats.length) {
    handleConfirm({
      selectedPermission,
      formats: newSelectedFormats,
    });
  } else {
    dispatch(clearItemInNewProduct({ fieldName }));
  }
};

const getCategorizedValues = (
  selectedFormats: DirectSalesCodeNameModel[] = [],
): Record<string, { included?: CodeNameModel[] | undefined; excluded?: CodeNameModel[] | undefined }> => {
  return sortByCategoryIncludeExclude(selectedFormats);
};

const getValue = (formats: DirectSalesCodeNameModel[]): DirectSalesCodeNameModel[] | undefined => {
  return formats.length ? formats : undefined;
};

export const ProductFormats: React.FC = () => {
  const dispatch = useDispatch();
  const onChangePanelVisibility = useOnChangePanelVisibility();
  const isReadOnly = useSelector(getIsReadOnly);
  const mediaType = useSelector((state: Store) => state.productManagement.newProduct.mediaType?.value);
  const channels = useSelector((state: Store) => state.productManagement.newProduct.channels?.value);
  const secondaryPanelType = useSelector((state: Store) => state.productManagement.secondaryPanel);
  const selectedFormats = useSelector((state: Store) => state.productManagement.newProduct.formats?.value);
  const formatSelectedPermission = useSelector(
    (state: Store) => state.productManagement.newProduct.formats?.selectionPermission,
  );
  const isFormatPanelOpen = secondaryPanelType === PlannerSecondaryPanelType.FORMAT;

  const categorizedValues = useMemo(() => getCategorizedValues(selectedFormats), [selectedFormats]);

  const handleConfirm = ({
    selectedPermission,
    formats,
  }: {
    selectedPermission: CodeNameModel;
    formats: DirectSalesCodeNameModel[];
  }): void => {
    dispatch(changeNewProductField({ fieldName, selectedPermission, value: getValue(formats) }));
    dispatch(hideSecondaryPanel());
  };

  const onRemoveFormatChip = (formatsToRemove?: string[], included?: boolean): void => {
    if (!formatSelectedPermission) return;

    if (!formatsToRemove) {
      if (included !== undefined) {
        handleFormatsChangeByInclude({
          selectedFormats: selectedFormats || [],
          included,
          dispatch,
          selectedPermission: formatSelectedPermission,
          handleConfirm,
        });
        return;
      }

      dispatch(clearItemInNewProduct({ fieldName }));
      return;
    }

    if (!formatsToRemove.length) return;

    handleFormatsChange({
      selectedFormats: selectedFormats || [],
      formatsToRemove,
      included,
      selectedPermission: { code: formatSelectedPermission, name: PERMISSION_LABELS[formatSelectedPermission] },
      handleConfirm,
      dispatch,
    });
  };

  return (
    <>
      <SectionButton
        isActive={isFormatPanelOpen}
        dataTestId="section-button-formats"
        theme={getTheme(formatSelectedPermission)}
        label="Format"
        icon={formatsSvg}
        onClick={() => onChangePanelVisibility(PlannerSecondaryPanelType.FORMAT)}
        isDisabled={!mediaType || !channels?.length}
        isEllipsisDisabled={isReadOnly}
        selected={
          isValueRequired(formatSelectedPermission)
            ? categorizedValues
            : getNoRestrictionsDisplayValue(formatSelectedPermission)
        }
        onClear={() => {
          dispatch(clearItemInNewProduct({ fieldName }));
          dispatch(hideSecondaryPanel());
        }}
        onRemoveChip={onRemoveFormatChip}
      />
      {isFormatPanelOpen && (
        <ProductFormatsPanel
          onConfirm={handleConfirm}
          onCancel={() => dispatch(hideSecondaryPanel())}
          isReadOnly={isReadOnly}
        />
      )}
    </>
  );
};
